// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import React from "react";
import ReactDOM from "react-dom/client";
import { App as AntApp, ConfigProvider, theme } from "antd";
import App from "./App";
import "antd/dist/reset.css";
import "./styles.css";
import { initNativeStorage } from "./native-storage";
import { isNativeApp } from "./server";
import { unlockAudioOnGesture } from "./calltones";
import { migrateWitnessStore } from "./transparency";

// One-time key-transparency witness migration (runs before anything reads the alarm):
// clears stale witnessed roots + a leftover FALSE equivocation alarm caused by the old
// in-memory STR chain resetting on a server restart. Version-gated, so it's a no-op
// after the first run. See migrateWitnessStore.
migrateWitnessStore();

// Arm the call-audio context from app start, so the FIRST gesture anywhere (typically the
// login click) unlocks it — otherwise a callee who hasn't clicked since loading can't be
// rung (browser autoplay block). Set up before login, not in the post-login Workspace.
unlockAudioOnGesture();

function render() {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
          token: {
            colorPrimary: "#19c37d",
            colorBgBase: "#0e1117",
            borderRadius: 6,
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          },
        }}
      >
        <AntApp>
          <App />
        </AntApp>
      </ConfigProvider>
    </React.StrictMode>,
  );
}

// In the native app, restore Keystore-backed secure storage into localStorage before
// the app reads it; on the web, render synchronously (unchanged behaviour).
if (isNativeApp()) {
  initNativeStorage().finally(render);
} else {
  render();
}

// Register the service worker (installable PWA + offline shell). Web only — the
// native app is served from its own local scheme, so it never registers (and never
// interferes with the Vite dev server / HMR / e2e).
if (import.meta.env.PROD && !isNativeApp() && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}
